import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import React from 'react';

// import { LoadingOutlined } from '@ant-design/icons';
import { Spin, Tabs } from 'antd';

import { useLazyGetDatesQuery } from 'services/events/eventsApiService';

import EventsLoadingState from 'containers/events-loading-state';

import s from './DateTab.module.scss';
import AllDateCard from './allDateCard';
import DateCard from './dateCard';

interface DateTabProps {
  eventId: string | undefined;
  // activeEvent: UserEventType;
  initialDateId?: string;
  setCurrentDateId?: Dispatch<SetStateAction<string | undefined>>;
  setCurrentDateName?: Dispatch<SetStateAction<string | undefined>>;
}

const DateTab: React.FC<DateTabProps> = ({
  eventId,
  initialDateId,
  setCurrentDateId,
  setCurrentDateName,
}) => {
  const [getDates, { data: dates, isFetching: loading }] = useLazyGetDatesQuery();
  const [sortedDates, setSortedDates] = useState({});
  const [activeDateId, setActiveDateId] = useState<string | null>(null);

  const handleDateCardClick = useCallback(
    (eventId, dateId) => {
      setActiveDateId(dateId);
      if (setCurrentDateId) {
        setCurrentDateId(dateId);
        const dateName = dates?.dates?.find((date) => date.dateId === dateId);
        setCurrentDateName!(dateName?.date);
      }
    },
    [setCurrentDateId, dates?.dates, setCurrentDateName],
  );

  const handleAllDateCardClick = useCallback(() => {
    setActiveDateId('allDates');
    if (setCurrentDateId && setCurrentDateName) {
      setCurrentDateId('allDates');
      setCurrentDateName('All dates');
    }
  }, [setCurrentDateId, setCurrentDateName]);

  const handleTabChange = useCallback(
    (key) => {
      if (
        key !== 'defaultKey' &&
        sortedDates &&
        eventId &&
        setCurrentDateId &&
        setCurrentDateName
      ) {
        const dateId = sortedDates[key][0].dateId;
        setActiveDateId(dateId);
        setCurrentDateId(dateId);
        const dateName = dates?.dates?.find((date) => date.dateId === dateId);
        setCurrentDateName(dateName?.date);
      } else if (
        key === 'defaultKey' &&
        eventId &&
        dates &&
        setCurrentDateId &&
        setCurrentDateName
      ) {
        if (dates.dates?.length > 1) {
          setActiveDateId('allDates');
          setCurrentDateId('allDates');
          setCurrentDateName('All dates');
        } else {
          setActiveDateId(dates.dates[0].dateId);
          setCurrentDateId!(dates.dates[0].dateId);
          const dateName = dates?.dates?.find((date) => date.dateId === dates.dates[0].dateId);
          setCurrentDateName(dateName?.date);
        }
      }
    },
    [sortedDates, eventId, dates, setCurrentDateName, setCurrentDateId],
  );

  useEffect(() => {
    if (eventId) {
      getDates(eventId);
    }
  }, [getDates, eventId]);

  useEffect(() => {
    const sortEvents = (dates) => {
      const sorted = dates.dates.reduce((acc, row) => {
        const month = row.date.split('-')[1]; // Extract month
        if (!acc[month]) {
          acc[month] = [];
        }
        acc[month].push(row);
        return acc;
      }, {});

      setSortedDates(sorted);
    };
    if (initialDateId && setCurrentDateName && setCurrentDateId && setActiveDateId) {
      setActiveDateId(initialDateId);
      setCurrentDateId(initialDateId);
      const dateName = dates?.dates?.find((date) => date.dateId === initialDateId);
      setCurrentDateName!(dateName?.date);
    } else if (dates && eventId) {
      sortEvents(dates);
      if (dates.dates?.length > 1) {
        setActiveDateId('allDates');
        if (setCurrentDateId && setCurrentDateName) {
          setCurrentDateId('allDates');
          setCurrentDateName('All dates');
        }
      } else {
        setActiveDateId(dates.dates[0].dateId);
        if (setCurrentDateId && setCurrentDateName) {
          setCurrentDateId(dates.dates[0].dateId);
          const dateName = dates?.dates?.find((date) => date.dateId === dates.dates[0].dateId);
          setCurrentDateName(dateName?.date);
        }
      }
    }
  }, [dates, initialDateId, eventId, setCurrentDateId, setCurrentDateName]);

  const getMonthName = (monthNumber) => {
    const date = new Date();
    date.setMonth(monthNumber - 1); // monthNumber is 1-based, while setMonth expects 0-based
    return date.toLocaleString('default', { month: 'long' });
  };

  return (
    <div>
      <Tabs
        defaultActiveKey="defaultKey"
        onChange={handleTabChange}
        tabBarStyle={{ color: '#8D949C' }}
        style={{ width: '100%' }}
        items={[
          {
            key: 'defaultKey',
            label: 'All dates',
            children: loading ? (
              <Spin indicator={<EventsLoadingState size={140} />} />
            ) : (
              <div className={s.dateCardTabPane}>
                {dates && eventId && dates.dates.length > 1 && (
                  <AllDateCard
                    startDate={dates.dates[0].date}
                    endDate={dates.dates[dates.dates.length - 1].date}
                    status={dates.availibilityStatus}
                    eventId={eventId}
                    onClick={handleAllDateCardClick}
                    isActive={activeDateId === 'allDates'}
                  />
                )}
                {dates &&
                  eventId &&
                  dates.dates.map((date, key) => (
                    <DateCard
                      key={key}
                      date={date.date}
                      status={date.status}
                      isPastDate={date.pastDate}
                      dateId={date.dateId}
                      eventId={eventId}
                      time={date.timeFrom}
                      onClick={handleDateCardClick}
                      isActive={activeDateId === date.dateId}
                    />
                  ))}
              </div>
            ),
          },
          ...Object.keys(sortedDates).map((month) => ({
            key: month,
            label: getMonthName(month),
            children: loading ? (
              <Spin indicator={<EventsLoadingState size={140} />} />
            ) : (
              <div className={s.dateCardTabPane}>
                {sortedDates[month] &&
                  eventId &&
                  sortedDates[month].map((date, key) => (
                    <DateCard
                      key={key}
                      date={date.date}
                      status={date.status}
                      dateId={date.dateId}
                      time={date.timeFrom}
                      isPastDate={date.pastDate}
                      eventId={eventId}
                      onClick={handleDateCardClick}
                      isActive={activeDateId === date.dateId}
                    />
                  ))}
              </div>
            ),
          })),
        ]}
      />
    </div>
  );
};

const areEqual = (prevProps, nextProps) => {
  return prevProps.eventType === nextProps.eventType && prevProps.eventId === nextProps.eventId;
};

const MemoizedDateTab = React.memo(DateTab, areEqual);

export default MemoizedDateTab;
